import { useCallback } from "react";

import { PackageItem } from "../../queries/useSearchPartPackagesByVehicle";
import { useTypedDispatch, useTypedSelector } from "../hooks";
import {
  CustomPart,
  PackageEditorEntry,
  PackageEditorQuantityRecord,
  VehicleManufacturerEntry,
  fallbackPackageEditorEntry,
  packageEditorSlice,
} from "../slices/packageEditorSlice";

export const usePackageEditor = (
  packageId: null | string
): [
  PackageEditorEntry,
  {
    addCustomPart(customPart: CustomPart): void;
    getPartQuantity(part: PackageItem): string;
    initializePackage(vehicleMapping: null | string[]): void;
    removeCustomPart(customPartNumber: string): void;
    setForceVehicleLoaderMode(forceVehicleLoaderMode: boolean): void;
    setModelsAreLoading(modelsAreLoading: boolean): void;
    setPartListTabIndex(partListTabIndex: number): void;
    setQuantity(quantity: PackageEditorQuantityRecord): void;
    setTabIndex(tabIndex: number): void;
    setVehicleManufacturers(
      vehicleManufacturers: VehicleManufacturerEntry[]
    ): void;
    setVehicleMapping(vehicleMapping: string[]): void;
    toggleVehicleManufacturerId(vehicleManufacturerId: number): void;
    toggleVehicleYearId(yearId: number): void;
    updateCustomPart(customPart: CustomPart): void;
  }
] => {
  const entry = useTypedSelector(({ packageEditor }) =>
    packageId
      ? packageEditor[packageId] || fallbackPackageEditorEntry
      : fallbackPackageEditorEntry
  );

  const dispatch = useTypedDispatch();

  const addCustomPart = useCallback(
    (customPart: CustomPart): void => {
      if (!packageId) {
        return;
      }
      const action = packageEditorSlice.actions.addCustomPart({
        customPart,
        packageId,
      });
      dispatch(action);
    },
    [packageId]
  );

  const initializePackage = useCallback(
    (vehicleMapping: null | string[]): void => {
      if (!packageId) {
        return;
      }
      const action = packageEditorSlice.actions.initializePackage({
        packageId,
        vehicleMapping,
      });
      dispatch(action);
    },
    [packageId]
  );

  const removeCustomPart = useCallback(
    (customPartNumber: string): void => {
      if (!packageId) {
        return;
      }
      const action = packageEditorSlice.actions.removeCustomPart({
        customPartNumber,
        packageId,
      });
      dispatch(action);
    },
    [packageId]
  );

  const setForceVehicleLoaderMode = useCallback(
    (forceVehicleLoaderMode: boolean): void => {
      if (!packageId) {
        return;
      }
      const action = packageEditorSlice.actions.setForceVehicleLoaderMode({
        forceVehicleLoaderMode,
        packageId,
      });
      dispatch(action);
    },
    [packageId]
  );

  const setModelsAreLoading = useCallback(
    (modelsAreLoading: boolean): void => {
      if (!packageId) {
        return;
      }
      const action = packageEditorSlice.actions.setModelsAreLoading({
        modelsAreLoading,
        packageId,
      });
      dispatch(action);
    },
    [packageId]
  );

  const setPartListTabIndex = useCallback(
    (partListTabIndex: number): void => {
      if (!packageId) {
        return;
      }
      const action = packageEditorSlice.actions.setPartListTabIndex({
        packageId,
        partListTabIndex,
      });
      dispatch(action);
    },
    [packageId]
  );

  const setQuantity = useCallback(
    (quantity: PackageEditorQuantityRecord): void => {
      if (!packageId) {
        return;
      }
      const action = packageEditorSlice.actions.setQuantity({
        packageId,
        quantity,
      });
      dispatch(action);
    },
    [packageId]
  );

  const setTabIndex = useCallback(
    (tabIndex: number): void => {
      if (!packageId) {
        return;
      }
      const action = packageEditorSlice.actions.setTabIndex({
        packageId,
        tabIndex,
      });
      dispatch(action);
    },
    [packageId]
  );

  const setVehicleManufacturers = useCallback(
    (vehicleManufacturers: VehicleManufacturerEntry[]): void => {
      if (!packageId) {
        return;
      }
      const action = packageEditorSlice.actions.setVehicleManufacturers({
        packageId,
        vehicleManufacturers,
      });
      dispatch(action);
    },
    [packageId]
  );

  const setVehicleMapping = useCallback(
    (vehicleMapping: string[]): void => {
      if (!packageId) {
        return;
      }
      const action = packageEditorSlice.actions.setVehicleMapping({
        packageId,
        vehicleMapping,
      });
      dispatch(action);
    },
    [packageId]
  );

  const toggleVehicleManufacturerId = useCallback(
    (vehicleManufacturerId: number): void => {
      if (!packageId) {
        return;
      }
      const action = packageEditorSlice.actions.toggleVehicleManufacturerId({
        packageId,
        vehicleManufacturerId,
      });
      dispatch(action);
    },
    [packageId]
  );

  const toggleVehicleYearId = useCallback(
    (yearId: number): void => {
      if (!packageId) {
        return;
      }
      const action = packageEditorSlice.actions.toggleYearId({
        packageId,
        yearId,
      });
      dispatch(action);
    },
    [packageId]
  );

  const updateCustomPart = useCallback(
    (customPart: CustomPart): void => {
      if (!packageId) {
        return;
      }
      const action = packageEditorSlice.actions.updateCustomPart({
        customPart,
        packageId,
      });
      dispatch(action);
    },
    [packageId]
  );

  const getPartQuantity = (part: PackageItem): string =>
    entry.quantity?.[part.itemId] || part.itemQuantity;

  return [
    entry,
    {
      addCustomPart,
      getPartQuantity,
      initializePackage,
      removeCustomPart,
      setForceVehicleLoaderMode,
      setModelsAreLoading,
      setPartListTabIndex,
      setQuantity,
      setTabIndex,
      setVehicleManufacturers,
      setVehicleMapping,
      toggleVehicleManufacturerId,
      toggleVehicleYearId,
      updateCustomPart,
    },
  ];
};
